import React, { useState, useEffect, useCallback } from 'react';
import {
  FiTablet, FiPlus, FiAlertCircle, FiRefreshCw, FiTrash2, FiCopy, FiCheck,
} from 'react-icons/fi';
import {
  getDevices, createDevice, updateDevice, rotateDeviceKey, deleteDevice, getCrews,
} from '../services/api';

// Data collector management, sat on the Team page under the crews it belongs to.
//
// A collector is company equipment that reports where it is, so the Command Center
// board can show a second account of where a crew is alongside what they clocked into.
// It is OBSERVATION ONLY — nothing a collector reports can start, stop or edit a timer,
// and that is what makes the whole thing safe to run. Crews keep their timers exactly
// as they always have.
//
// The device key is the one piece of state that needs care: it is shown once, when it
// is issued or rotated, and never again. It authenticates a beacon that runs with
// nobody logged in, so it cannot be a user token that expires when someone logs out.

const SIGNAL = {
  live:     { label: 'Reporting',    bg: '#f0fdf4', border: '#bbf7d0', fg: '#15803d' },
  stale:    { label: 'Late',         bg: '#fffbeb', border: '#fde68a', fg: '#b45309' },
  dark:     { label: 'No signal',    bg: '#fef2f2', border: '#fecaca', fg: '#b91c1c' },
  // Switched off on the tablet, and it told us so. A decision, not a fault.
  off:      { label: 'Switched off', bg: '#f9fafb', border: '#e5e7eb', fg: '#4b5563' },
  // Expected to be quiet. Neutral on purpose — see the board's is-quiet styling.
  deferred: { label: 'Reports later', bg: '#f9fafb', border: '#e5e7eb', fg: '#4b5563' },
  never:    { label: 'Never reported', bg: '#f9fafb', border: '#e5e7eb', fg: '#6b7280' },
};

function sinceLabel(iso) {
  if (!iso) return null;
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ${mins % 60}m ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// Shown once, immediately after the key is issued. Deliberately hard to miss and
// hard to lose: if it is dismissed before being copied, the only way forward is to
// rotate, which is by design — a key that can be looked up later is a key that leaks.
function KeyReveal({ reveal, onDismiss }) {
  const [copied, setCopied] = useState(false);
  if (!reveal) return null;
  return (
    <div style={{
      background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10,
      padding: 14, marginBottom: 14,
    }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
        Key for {reveal.name}
      </div>
      <div style={{ fontSize: 13, color: '#0c4a6e', marginBottom: 10 }}>
        Type or paste this into the beacon app on that collector. It is shown{' '}
        <strong>once</strong> — if you lose it, rotate the key and set it up again.
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
        <code style={{
          flex: 1, background: '#fff', border: '1px solid #bae6fd', borderRadius: 8,
          padding: '10px 12px', fontSize: 13, wordBreak: 'break-all', fontFamily: 'monospace',
        }}>{reveal.key}</code>
        <button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(reveal.key);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            } catch {
              setCopied(false);   // clipboard blocked; the key is on screen to read
            }
          }}
          style={{
            border: '1px solid #bae6fd', background: '#fff', borderRadius: 8,
            padding: '0 14px', cursor: 'pointer', fontSize: 13, color: '#0369a1',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
        >
          {copied ? <><FiCheck size={13} /> Copied</> : <><FiCopy size={13} /> Copy</>}
        </button>
        <button
          onClick={onDismiss}
          style={{
            border: '1px solid #d1d5db', background: '#fff', borderRadius: 8,
            padding: '0 14px', cursor: 'pointer', fontSize: 13,
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}

export default function DeviceManager() {
  const [devices, setDevices] = useState([]);
  const [crews, setCrews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCrew, setNewCrew] = useState('');
  const [reveal, setReveal] = useState(null);

  const load = useCallback(async () => {
    try {
      const [d, c] = await Promise.all([getDevices(), getCrews()]);
      setDevices(d.data || []);
      setCrews(c.data || []);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load collectors');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setBusy(true); setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'That did not save');
    }
    setBusy(false);
  };

  if (loading) return <div style={{ padding: 20, color: '#6b7280' }}>Loading collectors…</div>;

  return (
    <div style={{
      backgroundColor: 'white', borderRadius: 12, padding: 20,
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 24,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: 17, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiTablet /> Data Collectors
        </h2>
        <button
          onClick={() => setShowNew((v) => !v)}
          style={{
            marginLeft: 'auto', padding: '6px 12px', border: '1px solid #d1d5db',
            background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 13,
            display: 'inline-flex', alignItems: 'center', gap: 5,
          }}
        >
          <FiPlus size={13} /> Add collector
        </button>
      </div>
      <p style={{ color: '#6b7280', fontSize: 13, margin: '0 0 14px' }}>
        A collector reports where it is so the Command Center board can show a second
        account of where each crew is. It <strong>never touches timers</strong> — crews
        clock in and out exactly as they always have, and where the two disagree the
        board just says so.
      </p>

      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
          borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <FiAlertCircle /> {error}
        </div>
      )}

      <KeyReveal reveal={reveal} onDismiss={() => setReveal(null)} />

      {showNew && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Collector name (e.g. Collector 2)"
            style={{ flex: '2 1 200px', padding: 9, border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
          />
          <select
            value={newCrew}
            onChange={(e) => setNewCrew(e.target.value)}
            style={{ flex: '1 1 150px', padding: 9, border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
          >
            <option value="">Which crew carries it?</option>
            {crews.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button
            disabled={busy || !newName.trim()}
            onClick={() => run(async () => {
              const res = await createDevice({
                name: newName.trim(),
                crew_id: newCrew ? parseInt(newCrew, 10) : null,
              });
              setReveal({ name: res.data.name, key: res.data.device_key });
              setNewName(''); setNewCrew(''); setShowNew(false);
            })}
            style={{
              padding: '9px 16px', border: 'none', borderRadius: 8, fontWeight: 600,
              background: newName.trim() ? '#16a34a' : '#d1d5db', color: '#fff',
              cursor: newName.trim() ? 'pointer' : 'not-allowed',
            }}
          >
            Create
          </button>
        </div>
      )}

      {devices.length === 0 && (
        <div style={{ color: '#6b7280', fontStyle: 'italic' }}>
          No collectors yet. Add one, then put the key into the beacon app on that tablet.
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
        {devices.map((d) => {
          const sig = SIGNAL[d.signal] || SIGNAL.never;
          return (
            <div key={d.id} style={{
              flex: '1 1 300px', minWidth: 280, border: '1px solid #e5e7eb',
              borderRadius: 10, padding: 14, opacity: d.is_active ? 1 : 0.6,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{d.name}</div>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 5,
                  background: sig.bg, border: `1px solid ${sig.border}`, color: sig.fg,
                }}>{sig.label}</span>
              </div>

              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                {d.last_seen_at
                  ? <>Last heard {sinceLabel(d.last_seen_at)}
                      {d.last_battery != null && ` · battery ${d.last_battery}%`}
                      {d.app_version && ` · v${d.app_version}`}</>
                  : 'Waiting for its first report'}
              </div>

              {d.at_job_number && (
                <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
                  Currently at <strong>{d.at_job_number}</strong>
                </div>
              )}

              <select
                value={d.crew_id || ''}
                disabled={busy}
                onChange={(e) => run(() => updateDevice(d.id, {
                  crew_id: e.target.value ? parseInt(e.target.value, 10) : null,
                }))}
                title="Which crew carries this collector"
                style={{
                  width: '100%', marginTop: 10, padding: 8,
                  border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13,
                }}
              >
                <option value="">Not assigned to a crew</option>
                {crews.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {!d.crew_id && (
                <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>
                  Unassigned collectors don't appear on the board.
                </div>
              )}

              {/* Live vs deferred. A collector with no coverage, or one whose crew
                  keeps mobile data off for battery, is SUPPOSED to be quiet — and
                  saying "no signal" about it every day would blunt the warning on
                  every other card. */}
              <select
                value={d.mode || 'live'}
                disabled={busy}
                onChange={(e) => run(() => updateDevice(d.id, { mode: e.target.value }))}
                title="What silence from this collector means"
                style={{
                  width: '100%', marginTop: 8, padding: 8,
                  border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13,
                }}
              >
                <option value="live">Expect it to report continuously</option>
                <option value="deferred">Expect it to go quiet and catch up later</option>
              </select>

              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                <button
                  disabled={busy}
                  onClick={() => run(() => updateDevice(d.id, { is_active: !d.is_active }))}
                  style={{
                    flex: '1 1 auto', border: '1px solid #d1d5db', background: '#fff',
                    borderRadius: 6, padding: '5px 8px', fontSize: 12, cursor: 'pointer',
                  }}
                >
                  {d.is_active ? 'Retire' : 'Put back in service'}
                </button>
                <button
                  disabled={busy}
                  title="Issue a new key. The old one stops working immediately."
                  onClick={() => {
                    if (!window.confirm(
                      `Rotate the key for ${d.name}?\n\n`
                      + 'It will stop reporting until the new key is entered on the tablet.'
                    )) return;
                    run(async () => {
                      const res = await rotateDeviceKey(d.id);
                      setReveal({ name: res.data.name, key: res.data.device_key });
                    });
                  }}
                  style={{
                    border: '1px solid #d1d5db', background: '#fff', borderRadius: 6,
                    padding: '5px 8px', fontSize: 12, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}
                >
                  <FiRefreshCw size={11} /> New key
                </button>
                <button
                  disabled={busy}
                  title="Delete this collector (its recorded visits are kept)"
                  onClick={() => {
                    if (!window.confirm(
                      `Delete ${d.name}?\n\n`
                      + 'Where it has been is kept, so past days still read correctly.'
                    )) return;
                    run(() => deleteDevice(d.id));
                  }}
                  style={{
                    border: '1px solid #fecaca', background: '#fff', color: '#dc2626',
                    borderRadius: 6, padding: '5px 8px', fontSize: 12, cursor: 'pointer',
                  }}
                >
                  <FiTrash2 size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
